import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import { SnackbarService } from '../../core/services';
import * as TaskActions from './task.actions';
import { TaskApi } from './task.api';
import type { ApiResponse, TaskAttachmentUploadResponse, TaskItem, TaskNotification, TaskProject, TaskSprint } from './task.models';

@Injectable()
export class TaskEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(TaskApi);
  private readonly snackbar = inject(SnackbarService);

  private toErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object') {
      if ('error' in error) {
        const nested = (error as { error?: { message?: unknown } }).error?.message;
        if (typeof nested === 'string') {
          return nested;
        }
      }
      if ('message' in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === 'string') {
          return message;
        }
      }
    }
    return 'Unknown error';
  }

  fetchProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskProjectsFetchRequest),
      switchMap(() =>
        this.api.listProjects().pipe(
          map((response: ApiResponse<TaskProject[]>) => TaskActions.TaskProjectsFetchSuccess({ projects: response.data ?? [] })),
          catchError((error) => of(TaskActions.TaskProjectsFetchFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  createProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskProjectCreateRequest),
      switchMap(({ payload, successMessage }) =>
        this.api.createProject(payload).pipe(
          map((response: ApiResponse<TaskProject>) => TaskActions.TaskProjectCreateSuccess({ project: response.data, successMessage })),
          catchError((error) => of(TaskActions.TaskProjectCreateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  updateProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskProjectUpdateRequest),
      switchMap(({ projectId, payload, successMessage, silentSuccess }) =>
        this.api.updateProject(projectId, payload).pipe(
          map((response: ApiResponse<TaskProject>) => TaskActions.TaskProjectUpdateSuccess({ project: response.data, successMessage, silentSuccess })),
          catchError((error) => of(TaskActions.TaskProjectUpdateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  fetchTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskItemsFetchRequest),
      switchMap(({ projectId }) =>
        this.api.listTasks(projectId).pipe(
          map((response: ApiResponse<TaskItem[]>) => TaskActions.TaskItemsFetchSuccess({ tasks: response.data ?? [] })),
          catchError((error) => of(TaskActions.TaskItemsFetchFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  fetchSprints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskSprintsFetchRequest),
      switchMap(({ projectId }) =>
        this.api.listSprints(projectId).pipe(
          map((response: ApiResponse<TaskSprint[]>) => TaskActions.TaskSprintsFetchSuccess({ sprints: response.data ?? [] })),
          catchError((error) => of(TaskActions.TaskSprintsFetchFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  fetchNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskNotificationsFetchRequest),
      switchMap(() =>
        this.api.listNotifications().pipe(
          map((response: ApiResponse<TaskNotification[]>) => TaskActions.TaskNotificationsFetchSuccess({ notifications: response.data ?? [] })),
          catchError((error) => of(TaskActions.TaskNotificationsFetchFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  fetchTaskDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskDetailFetchRequest),
      switchMap(({ taskId }) =>
        this.api.getTask(taskId).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskDetailFetchSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskDetailFetchFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  createTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskCreateRequest),
      switchMap(({ payload, successMessage }) =>
        this.api.createTask(payload).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskCreateSuccess({ task: response.data, successMessage })),
          catchError((error) => of(TaskActions.TaskCreateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  createSprint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskSprintCreateRequest),
      switchMap(({ projectId, payload, successMessage }) =>
        this.api.createSprint(projectId, payload).pipe(
          map((response: ApiResponse<TaskSprint>) => TaskActions.TaskSprintCreateSuccess({ sprint: response.data, successMessage })),
          catchError((error) => of(TaskActions.TaskSprintCreateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  updateSprint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskSprintUpdateRequest),
      switchMap(({ projectId, sprintId, payload, successMessage, silentSuccess }) =>
        this.api.updateSprint(projectId, sprintId, payload).pipe(
          map((response: ApiResponse<TaskSprint>) => TaskActions.TaskSprintUpdateSuccess({ sprint: response.data, successMessage, silentSuccess })),
          catchError((error) => of(TaskActions.TaskSprintUpdateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  updateTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskUpdateRequest),
      switchMap(({ taskId, payload, successMessage, silentSuccess }) =>
        this.api.updateTask(taskId, payload).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskUpdateSuccess({ task: response.data, successMessage, silentSuccess })),
          catchError((error) => of(TaskActions.TaskUpdateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  addComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskCommentCreateRequest),
      switchMap(({ taskId, body }) =>
        this.api.addComment(taskId, { body }).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskCommentCreateSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskCommentCreateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  addSubtask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskSubtaskCreateRequest),
      switchMap(({ taskId, title, assignee_user_id }) =>
        this.api.addSubtask(taskId, { title, assignee_user_id }).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskSubtaskCreateSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskSubtaskCreateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  updateSubtask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskSubtaskUpdateRequest),
      switchMap(({ taskId, subtaskId, title, is_completed, assignee_user_id }) =>
        this.api.updateSubtask(taskId, subtaskId, { title, is_completed, assignee_user_id }).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskSubtaskUpdateSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskSubtaskUpdateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  deleteSubtask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskSubtaskDeleteRequest),
      switchMap(({ taskId, subtaskId }) =>
        this.api.deleteSubtask(taskId, subtaskId).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskSubtaskDeleteSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskSubtaskDeleteFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  uploadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskAttachmentUploadRequest),
      switchMap(({ taskId, file }) =>
        this.api.uploadAttachment(taskId, file).pipe(
          map((response: ApiResponse<TaskAttachmentUploadResponse>) => TaskActions.TaskAttachmentUploadSuccess({ response: response.data })),
          catchError((error) => of(TaskActions.TaskAttachmentUploadFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  addTimeEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskTimeEntryCreateRequest),
      switchMap(({ taskId, payload }) =>
        this.api.addTimeEntry(taskId, payload).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskTimeEntryCreateSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskTimeEntryCreateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  updateTimeEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskTimeEntryUpdateRequest),
      switchMap(({ taskId, timeEntryId, payload }) =>
        this.api.updateTimeEntry(taskId, timeEntryId, payload).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskTimeEntryUpdateSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskTimeEntryUpdateFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  deleteTimeEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskTimeEntryDeleteRequest),
      switchMap(({ taskId, timeEntryId }) =>
        this.api.deleteTimeEntry(taskId, timeEntryId).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskTimeEntryDeleteSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskTimeEntryDeleteFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  startTimer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskTimerStartRequest),
      switchMap(({ taskId, payload }) =>
        this.api.startTimer(taskId, payload).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskTimerStartSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskTimerStartFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  stopTimer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskTimerStopRequest),
      switchMap(({ taskId, payload }) =>
        this.api.stopTimer(taskId, payload).pipe(
          map((response: ApiResponse<TaskItem>) => TaskActions.TaskTimerStopSuccess({ task: response.data })),
          catchError((error) => of(TaskActions.TaskTimerStopFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  markNotificationRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskNotificationMarkReadRequest),
      switchMap(({ notificationId }) =>
        this.api.markNotificationRead(notificationId).pipe(
          map((response: ApiResponse<TaskNotification>) => TaskActions.TaskNotificationMarkReadSuccess({ notification: response.data })),
          catchError((error) => of(TaskActions.TaskNotificationMarkReadFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  markAllNotificationsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskNotificationsMarkAllReadRequest),
      switchMap(() =>
        this.api.markAllNotificationsRead().pipe(
          map(() => TaskActions.TaskNotificationsMarkAllReadSuccess()),
          catchError((error) => of(TaskActions.TaskNotificationsMarkAllReadFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  clearNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.TaskNotificationsClearRequest),
      switchMap(() =>
        this.api.clearNotifications().pipe(
          map(() => TaskActions.TaskNotificationsClearSuccess()),
          catchError((error) => of(TaskActions.TaskNotificationsClearFailure({ error: this.toErrorMessage(error) }))),
        ),
      ),
    ),
  );

  successNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TaskActions.TaskProjectCreateSuccess,
          TaskActions.TaskProjectUpdateSuccess,
          TaskActions.TaskSprintCreateSuccess,
          TaskActions.TaskSprintUpdateSuccess,
          TaskActions.TaskCreateSuccess,
          TaskActions.TaskUpdateSuccess,
        ),
        map((action) => {
          const successMessage = 'successMessage' in action ? action.successMessage : undefined;
          const silentSuccess = 'silentSuccess' in action ? action.silentSuccess : false;
          if (successMessage && !silentSuccess) {
            this.snackbar.showNotification('success', '', successMessage, 'Close');
          }
        }),
      ),
    { dispatch: false },
  );

  failureNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TaskActions.TaskProjectsFetchFailure,
          TaskActions.TaskProjectCreateFailure,
          TaskActions.TaskProjectUpdateFailure,
          TaskActions.TaskSprintsFetchFailure,
          TaskActions.TaskSprintCreateFailure,
          TaskActions.TaskSprintUpdateFailure,
          TaskActions.TaskItemsFetchFailure,
          TaskActions.TaskDetailFetchFailure,
          TaskActions.TaskCreateFailure,
          TaskActions.TaskUpdateFailure,
          TaskActions.TaskCommentCreateFailure,
          TaskActions.TaskSubtaskCreateFailure,
          TaskActions.TaskSubtaskUpdateFailure,
          TaskActions.TaskSubtaskDeleteFailure,
          TaskActions.TaskAttachmentUploadFailure,
          TaskActions.TaskTimeEntryCreateFailure,
          TaskActions.TaskTimeEntryUpdateFailure,
          TaskActions.TaskTimeEntryDeleteFailure,
          TaskActions.TaskTimerStartFailure,
          TaskActions.TaskTimerStopFailure,
          TaskActions.TaskNotificationsFetchFailure,
          TaskActions.TaskNotificationMarkReadFailure,
          TaskActions.TaskNotificationsMarkAllReadFailure,
          TaskActions.TaskNotificationsClearFailure,
        ),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Task error: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );
}
